import type { Diagnosis, RepairChange, StepId, StepResult, UploadedBackupPayload } from "../../api/repair";
import { formatBackupTime } from "./downloads";

export type UploadedBackup = {
  name: string;
  folderCount: number;
  documentCount: number;
  payload: UploadedBackupPayload;
};

export type StepStatus = "pending" | "ready" | "in-progress" | "complete" | "skipped" | "failed";

export type PlanStep = {
  id: StepId;
  title: string;
  description: string;
  severity: "red" | "yellow" | "green";
  status: StepStatus;
  required: boolean;
  preview: string[];
  result?: StepResult;
  error?: string;
};

export type TranscriptEntry = {
  stepId: StepId;
  title: string;
  action: string;
  count: number;
  changed: RepairChange[];
  at: string;
};

export type RepairState = {
  phase: "diagnose" | "execute" | "done";
  diagnosis: Diagnosis | null;
  uploadedBackup: UploadedBackup | null;
  pitrTimestamp: number | null;
  planSteps: PlanStep[];
  loading: boolean;
  error: string | null;
  transcript: TranscriptEntry[];
  activeStepId: StepId | null;
};

export type RepairAction =
  | { type: "start" }
  | { type: "diagnose-success"; diagnosis: Diagnosis }
  | { type: "diagnose-error"; error: string }
  | { type: "goto-diagnose" }
  | { type: "goto-execute" }
  | { type: "step-started"; stepId: StepId }
  | { type: "step-preview-loaded"; stepId: StepId; result: StepResult }
  | { type: "step-applied"; stepId: StepId; result: StepResult }
  | { type: "step-error"; stepId: StepId; error: string }
  | { type: "step-skip"; stepId: StepId }
  | { type: "diagnosis-refreshed"; diagnosis: Diagnosis }
  | { type: "set-uploaded-backup"; backup: UploadedBackup | null }
  | { type: "set-pitr-timestamp"; timestamp: number | null }
  | { type: "finish" }
  | { type: "reset" };

export const initialRepairState: RepairState = {
  phase: "diagnose",
  diagnosis: null,
  uploadedBackup: null,
  pitrTimestamp: null,
  planSteps: [],
  loading: false,
  error: null,
  transcript: [],
  activeStepId: null,
};

/**
 * Derives the ordered plan from a diagnosis. Order is hardcoded:
 * missing → external → folders → trash. Severity reflects whether
 * the step is "must-fix" (red), "review" (yellow), or "clean" (green).
 *
 * An optional uploadedBackup supersedes the automatic backup info for
 * the restore-folders step — so a user can bring their own JSON when
 * the automatic backup is unavailable or too stale.
 *
 * `pitrTimestamp` signals the admin has explicitly picked an automatic
 * backup time. Without an upload or a picked timestamp, the restore-folders
 * step stays skipped and hidden — most repairs don't need it, and showing it
 * by default just adds confusing UI.
 */
export function derivePlanSteps(
  diagnosis: Diagnosis,
  uploadedBackup: UploadedBackup | null = null,
  pitrTimestamp: number | null = null
): PlanStep[] {
  const missingCount = diagnosis.missingFromTree.length;
  const externalCount = diagnosis.externalDocs.length;
  const folderCount = diagnosis.folders.count;
  const pitrBackup = diagnosis.folders.backup;
  const backupExplicitlyChosen = !!uploadedBackup || pitrTimestamp != null;
  const backupSource = uploadedBackup
    ? {
        available: true,
        folderCount: uploadedBackup.folderCount,
        documentCount: uploadedBackup.documentCount,
        readTime: `upload: ${uploadedBackup.name}`,
      }
    : pitrBackup;

  const steps: PlanStep[] = [];

  steps.push({
    id: "restore-missing",
    title: "Restore missing documents to root",
    description:
      missingCount > 0
        ? `${missingCount} document${missingCount === 1 ? "" : "s"} will be added back at the project root.`
        : "No documents are missing from the tree.",
    severity: missingCount > 0 ? "red" : "green",
    status: missingCount > 0 ? "pending" : "skipped",
    required: missingCount > 0,
    preview: diagnosis.missingFromTree.map((d) => d.title),
  });

  steps.push({
    id: "remove-external",
    title: "Remove external documents",
    description:
      externalCount > 0
        ? `${externalCount} document${externalCount === 1 ? "" : "s"} belonging to other projects will be unlinked.`
        : "No external documents are contaminating this project.",
    severity: externalCount > 0 ? "red" : "green",
    status: externalCount > 0 ? "pending" : "skipped",
    required: externalCount > 0,
    preview: diagnosis.externalDocs.map((d) => d.title),
  });

  // Offer restore only when the admin has explicitly picked a backup (upload
  // or automatic timestamp). The backup may remap documents into different
  // folders than where they currently live (e.g. admin moved docs out of
  // folders to root). The dry-run reveals whether there are actual changes.
  const canRestoreFolders = backupExplicitlyChosen && backupSource.available && backupSource.folderCount > 0;
  const backupLabel = uploadedBackup ? `upload: ${uploadedBackup.name}` : formatBackupTime(backupSource.readTime);
  const restoreDescription = canRestoreFolders
    ? folderCount === 0
      ? `0 folders currently. Backup has ${backupSource.folderCount} folder${backupSource.folderCount === 1 ? "" : "s"} from ${backupLabel}.`
      : `${folderCount} folder${folderCount === 1 ? "" : "s"} present. Backup from ${backupLabel} may remap document placement — dry-run to see what would change.`
    : "No backup available.";
  steps.push({
    id: "restore-folders",
    title: "Restore folder structure from backup",
    description: restoreDescription,
    severity: canRestoreFolders ? "yellow" : "green",
    status: canRestoreFolders ? "pending" : "skipped",
    required: false,
    preview: canRestoreFolders ? [`${backupSource.folderCount} folders · ${backupSource.documentCount} documents from ${backupLabel}`] : [],
  });

  // First pending step becomes ready.
  const firstPending = steps.findIndex((s) => s.status === "pending");
  if (firstPending >= 0) {
    steps[firstPending] = { ...steps[firstPending], status: "ready" };
  }

  return steps;
}

/**
 * Enforces the ordered checklist: a step becomes "ready" only once
 * all earlier required steps are "complete" or "skipped".
 */
function advanceReadiness(steps: PlanStep[]): PlanStep[] {
  const result = [...steps];
  let blocked = false;
  for (let i = 0; i < result.length; i++) {
    const s = result[i];
    if (s.status === "complete" || s.status === "skipped") continue;
    if (s.status === "in-progress" || s.status === "failed") {
      blocked = true;
      continue;
    }
    // pending or ready
    if (!blocked) {
      result[i] = { ...s, status: "ready" };
      blocked = true; // only the first non-done step is ready
    } else {
      result[i] = { ...s, status: "pending" };
    }
  }
  return result;
}

function transcriptFromStep(step: PlanStep, result: StepResult): TranscriptEntry {
  const count = result.changed.length;
  return {
    stepId: step.id,
    title: step.title,
    action: step.id,
    count,
    changed: result.changed,
    at: new Date().toISOString(),
  };
}

export function repairReducer(state: RepairState, action: RepairAction): RepairState {
  switch (action.type) {
    case "start":
      return { ...state, loading: true, error: null };

    case "diagnose-success": {
      const planSteps = derivePlanSteps(action.diagnosis, state.uploadedBackup, state.pitrTimestamp);
      return {
        ...state,
        loading: false,
        error: null,
        diagnosis: action.diagnosis,
        planSteps,
      };
    }

    case "diagnose-error":
      return { ...state, loading: false, error: action.error };

    case "goto-diagnose":
      return { ...state, phase: "diagnose" };

    case "goto-execute":
      return { ...state, phase: "execute" };

    case "step-started":
      return {
        ...state,
        activeStepId: action.stepId,
        planSteps: state.planSteps.map((s) => (s.id === action.stepId ? { ...s, status: "in-progress", error: undefined } : s)),
      };

    case "step-preview-loaded":
      // Silently stores a dry-run result on a step without changing its status.
      // Used to pre-load the restore-folders preview so the admin sees moves/
      // tree inline without having to click a separate "Dry run" button first.
      return {
        ...state,
        planSteps: state.planSteps.map((s) => (s.id === action.stepId ? { ...s, result: action.result } : s)),
      };

    case "step-applied": {
      const step = state.planSteps.find((s) => s.id === action.stepId);
      if (!step) return state;
      const diagnosis = action.result.audit ?? state.diagnosis;
      const updated = state.planSteps.map((s) => (s.id === action.stepId ? { ...s, status: "complete" as const, result: action.result } : s));
      const withReadiness = advanceReadiness(updated);
      return {
        ...state,
        activeStepId: null,
        diagnosis,
        planSteps: withReadiness,
        transcript: [...state.transcript, transcriptFromStep(step, action.result)],
      };
    }

    case "step-error":
      return {
        ...state,
        activeStepId: null,
        planSteps: state.planSteps.map((s) => (s.id === action.stepId ? { ...s, status: "failed", error: action.error } : s)),
      };

    case "step-skip": {
      const updated = state.planSteps.map((s) => (s.id === action.stepId ? { ...s, status: "skipped" as const } : s));
      return { ...state, planSteps: advanceReadiness(updated) };
    }

    case "diagnosis-refreshed": {
      // Preserve the completed/skipped status of existing steps; re-derive readiness for the rest.
      const fresh = derivePlanSteps(action.diagnosis, state.uploadedBackup, state.pitrTimestamp);
      const merged = fresh.map((f) => {
        const prev = state.planSteps.find((p) => p.id === f.id);
        if (prev && (prev.status === "complete" || prev.status === "skipped")) {
          return { ...f, status: prev.status, result: prev.result };
        }
        return f;
      });
      return { ...state, diagnosis: action.diagnosis, planSteps: advanceReadiness(merged) };
    }

    case "set-uploaded-backup": {
      if (!state.diagnosis) {
        return { ...state, uploadedBackup: action.backup };
      }
      // Only restore-folders depends on the backup source — re-derive just
      // that step while preserving the user's choices on everything else.
      const fresh = derivePlanSteps(state.diagnosis, action.backup, state.pitrTimestamp);
      const merged = fresh.map((f) => {
        if (f.id === "restore-folders") {
          const prev = state.planSteps.find((p) => p.id === f.id);
          if (prev && prev.status === "complete") return { ...f, status: prev.status, result: prev.result };
          return f;
        }
        const prev = state.planSteps.find((p) => p.id === f.id);
        return prev ?? f;
      });
      return { ...state, uploadedBackup: action.backup, planSteps: advanceReadiness(merged) };
    }

    case "set-pitr-timestamp": {
      // Re-derive plan so restore-folders flips on when a timestamp is picked
      // and off when it's cleared. Preserve completed/skipped statuses on the
      // unrelated steps.
      if (!state.diagnosis) return { ...state, pitrTimestamp: action.timestamp };
      const fresh = derivePlanSteps(state.diagnosis, state.uploadedBackup, action.timestamp);
      const merged = fresh.map((f) => {
        if (f.id === "restore-folders") {
          const prev = state.planSteps.find((p) => p.id === f.id);
          if (prev && prev.status === "complete") return { ...f, status: prev.status, result: prev.result };
          return f;
        }
        const prev = state.planSteps.find((p) => p.id === f.id);
        return prev ?? f;
      });
      return { ...state, pitrTimestamp: action.timestamp, planSteps: advanceReadiness(merged) };
    }

    case "finish":
      return { ...state, phase: "done" };

    case "reset":
      return initialRepairState;

    default:
      return state;
  }
}

/** Checks whether all required steps are resolved (complete or skipped). */
export function isPlanComplete(steps: PlanStep[]): boolean {
  return steps.every((s) => !s.required || s.status === "complete" || s.status === "skipped");
}
